import type { QueryClient } from '@tanstack/react-query';
import type { PlayerDto, PlayerXpEarnedContext } from './player.schema';
import { replaceEqualDeep } from '@tanstack/react-query';
import { QueriesKeys } from '../queriesKeys';

// At most one early XP snapshot per QueryClient, scoped by its userId. Keep it
// across profile responses; the socket hook clears it on logout or unmount.
// This is not a second UI store and never starts a request.
const pendingProgress = new WeakMap<QueryClient, PlayerXpEarnedContext>();

type PlayerProgress = Pick<PlayerDto, 'userId' | 'totalXp' | 'level' | 'currentLevelXpRequired' | 'nextLevelXpRequired'>;

function mergePlayerProgress(profile: PlayerDto, progress: PlayerProgress): PlayerDto {
  // XP only increases in the current contract. Keep the level and thresholds
  // from the same snapshot; status/media still come from the full profile.
  if (profile.userId !== progress.userId || profile.totalXp >= progress.totalXp) {
    return profile;
  }

  return {
    ...profile,
    totalXp: progress.totalXp,
    level: progress.level,
    currentLevelXpRequired: progress.currentLevelXpRequired,
    nextLevelXpRequired: progress.nextLevelXpRequired,
  };
}

export function mergePlayerQueryData(previous: PlayerDto | undefined, incoming: PlayerDto): PlayerDto {
  return replaceEqualDeep(previous, previous ? mergePlayerProgress(incoming, previous) : incoming);
}

function rememberPendingPlayerProgress(client: QueryClient, progress: PlayerXpEarnedContext) {
  const previous = pendingProgress.get(client);
  if (!previous || previous.userId !== progress.userId || progress.totalXp > previous.totalXp) {
    pendingProgress.set(client, progress);
  }
}

export function applyPlayerXpEvent(
  client: QueryClient,
  progress: PlayerXpEarnedContext,
  currentUserId: string | null,
) {
  const profile = client.getQueryData<PlayerDto>([QueriesKeys.mePlayerProfile]);
  // Preserve the cached-identity fallback when the access token has expired.
  const userId = currentUserId ?? profile?.userId;
  if (progress.userId !== userId) {
    return;
  }

  // Never seed a partial PlayerDto or refetch just because an event arrived first.
  if (!profile || profile.userId !== progress.userId) {
    rememberPendingPlayerProgress(client, progress);
    return;
  }

  client.setQueryData<PlayerDto>([QueriesKeys.mePlayerProfile], currentProfile => (
    currentProfile ? mergePlayerProgress(currentProfile, progress) : currentProfile
  ));
}

export function applyPendingPlayerProgress(client: QueryClient, profile: PlayerDto): PlayerDto {
  const progress = pendingProgress.get(client);
  return progress ? mergePlayerProgress(profile, progress) : profile;
}

export function clearPendingPlayerProgress(client: QueryClient) {
  pendingProgress.delete(client);
}
